/**
 * Download Gate Alpine Component
 * ==============================
 * Collects user email and consent before allowing file downloads.
 * Creates a lead in Pipedrive, then triggers the download.
 *
 * Follows the same state pattern as form.js but with download-specific behavior.
 *
 * Usage in Twig:
 * <form x-data="downloadGate()" x-ref="form" @submit.prevent="submit">
 *   ...
 * </form>
 */

const STATE_IDLE = 'idle'
const STATE_LOADING = 'loading'
const STATE_SUCCESS = 'success'
const STATE_ERROR = 'error'

export default () => ({
  // State
  state: STATE_IDLE,
  errorMessage: '',

  // Form fields for validation
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  privacyConsent: false,
  dataProcessingConsent: false,

  // Computed: check if form is valid
  get isValid() {
    return this.salutation !== '' &&
           this.firstName.trim() !== '' &&
           this.lastName.trim() !== '' &&
           this.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) &&
           this.privacyConsent &&
           this.dataProcessingConsent
  },

  // State helpers (matching form.js pattern)
  isIdle() {
    return this.state === STATE_IDLE
  },

  isLoading() {
    return this.state === STATE_LOADING
  },

  isSuccess() {
    return this.state === STATE_SUCCESS
  },

  isError() {
    return this.state === STATE_ERROR
  },

  // Computed: check if both consents are checked (for button disabled state)
  get loading() {
    return this.state === STATE_LOADING
  },

  get success() {
    return this.state === STATE_SUCCESS
  },

  get error() {
    return this.state === STATE_ERROR
  },

  /**
   * Submit the form
   */
  async submit() {
    // Prevent double submission
    if (!this.isIdle()) return

    // Get form reference and validate
    const form = this.$refs.form
    if (!form || !form.checkValidity()) return

    this.state = STATE_LOADING
    this.errorMessage = ''

    // Get form values via FormData
    const formData = new FormData(form)

    try {
      // Add download info to form data (CSRF token already in form via csrfInput())
      formData.append('downloadUrl', this.$store.modal.data?.url || '')
      formData.append('downloadTitle', this.$store.modal.data?.title || '')
      formData.append('pageUrl', window.location.href)

      const response = await fetch('/actions/pipedrive/form/create-download-lead', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })

      if (response.ok) {
        // GTM conversion event — must precede the tab-opening download
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'download_lead_success',
          form_name: 'dokument_download',
          file_name: this.$store.modal.data?.title || '',
          file_url: this.$store.modal.data?.url || '',
          page_path: window.location.pathname
        })
        this.state = STATE_SUCCESS
        this.triggerDownload(1500)
      } else {
        // API error but allow download anyway (graceful degradation)
        console.error('Download gate API error:', response.status)
        this.state = STATE_ERROR
        this.errorMessage = 'Ein Fehler ist aufgetreten.'
        this.triggerDownload(2000)
      }
    } catch (error) {
      // Network error but allow download anyway
      console.error('Download gate error:', error)
      this.state = STATE_ERROR
      this.errorMessage = error.message || 'Ein Fehler ist aufgetreten.'
      this.triggerDownload(2000)
    }
  },

  /**
   * Trigger the download and close modal
   */
  triggerDownload(delay) {
    setTimeout(() => {
      window.open(this.$store.modal.data?.url, '_blank')
      this.$store.modal.close()
      this.reset()
    }, delay)
  },

  /**
   * Reset form state
   */
  reset() {
    this.state = STATE_IDLE
    this.errorMessage = ''
    this.salutation = ''
    this.firstName = ''
    this.lastName = ''
    this.email = ''
    this.privacyConsent = false
    this.dataProcessingConsent = false
  }
})
